"use client"

import { Fragment, useEffect, useMemo, useState } from "react"
// import { Button, Card, CardBody, CardHeader, Col, Container, Form, FormGroup, Input, Label, Row, Table } from "reactstrap"
import { Button, Card, CardBody, CardHeader, Col, Container, Form, FormGroup, Input, Label, Row, Table, Modal, ModalHeader, ModalBody, ModalFooter, Pagination, PaginationItem, PaginationLink } from "reactstrap"
import Loader from "../../shared/Loader"
import { errorToast } from "../../shared/Toast"
import ToggleSwitch from "../../shared/ToggleSwitch"
import Breadcrumb from "../common/breadcrumb"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import MultiSelectField from "../common/MultiSelectField"
import { ChevronDown, Trash2, Eye, EyeOff } from "react-feather"
import { getCategoryListing, getLoyaltyById, getMerchantListing, getProductListing, updateLoyalty } from "../../utils/apiCalls"
import { C, reportingGlobalCSS } from "../reporting/reportingStyles"

/**
 * What the two rates actually cost, as a percentage of spend.
 *
 * The earn rate and the redeem rate are independent numbers, and their RATIO is the only
 * figure that costs money: earn 10 per SAR and give a SAR back for 200 points and you are
 * paying 5%. Nothing on this screen said so, which is how a tier ladder of 10 / 50 / 100
 * points per SAR against a single redeem rate turns into 5% / 25% / 50% without anyone
 * meaning it.
 *
 * Derived, never stored — there is no third field to keep in step.
 */
const cashbackPercent = (pointsPerSar, redeemPointsPerSar) => {
    const earn = Number(pointsPerSar)
    const redeem = Number(redeemPointsPerSar)
    if (!Number.isFinite(earn) || !Number.isFinite(redeem) || earn <= 0 || redeem <= 0) return null
    const pct = (earn / redeem) * 100
    // Two decimals: 0.05 points per SAR against 50 is 0.1%, and rounding to a whole number
    // would print every small programme as 0%.
    return Number.isInteger(pct) ? String(pct) : pct.toFixed(2).replace(/\.?0+$/, "")
}

const Edit_Loyalty = () => {
    const navigate = useNavigate()
    const { loyaltyId } = useParams();
    // const location = useLocation();
    // const { merchantId: stateMerchantId } = location.state || {};
    const [searchParams] = useSearchParams();

    const merchantId = searchParams.get("merchantId");

    const userType = localStorage.getItem("userType");
    const loggedInMerchantId = localStorage.getItem("MerchantId");

    const [loading, setLoading] = useState(false)
    const [showRedeemTable, setShowRedeemTable] = useState(true)
    const [loyaltyData, setLoyaltyData] = useState({
        merchantId: "",
        name: "",
        arabicName: "",
        loyaltyType: "",
        eligibleLoyaltyAmount: "",
        basePointsPerSar: "",
        useTiersForPercentage: false,
        percentageTiers: [],
        rewardOnStampsCount: "",
        useStampTiers: false,
        stampTiers: [],
        eligibleMode: "all",
        products: [],
        categories: [],
        redeemableProductPercentage: "",
        redeemProducts: [],
        isActive: true,
        voucherExpiryDays: 30,
        voucherExpiryPolicy: "forfeit",
        rewardIncludesModifiers: false,
        earnOnNetOfDiscounts: false,
    })
    // loyaltyData.voucherExpiryDays = loyaltyData.voucherExpiryDays ?? 30;
    // loyaltyData.voucherExpiryPolicy = loyaltyData.voucherExpiryPolicy ?? "forfeit";

    const [merchantData, setMerchantData] = useState([]);
    const [products, setProducts] = useState([])
    const [categories, setCategories] = useState([])
    const [productModalOpen, setProductModalOpen] = useState(false)
    const [modalType, setModalType] = useState(null) // 'eligible' or 'redeemable'
    const [modalSearch, setModalSearch] = useState("")
    const [modalCategory, setModalCategory] = useState("")
    const [tempSelectedIds, setTempSelectedIds] = useState(new Set())
    const [modalProducts, setModalProducts] = useState([])
    const [modalPage, setModalPage] = useState(1)
    const [modalLimit, setModalLimit] = useState(10)
    const [modalTotalCount, setModalTotalCount] = useState(0)
    const [isModalLoading, setIsModalLoading] = useState(false)

    useEffect(() => {
        if (loyaltyId && merchantId) {
            setLoading(true);
            getLoyaltyById(loyaltyId, merchantId).then(data => {
                if (data) {

                    setLoyaltyData({
                        merchantId: data.merchantId,
                        name: data.name || '',
                        arabicName: data.arabicName || '',
                        loyaltyType: data.loyaltyType || '',
                        eligibleLoyaltyAmount: data.eligibleLoyaltyAmount || '',
                        // New field first, old one as a fallback: a programme that has not
                        // been re-saved since the convention changed still only has the
                        // percentage, and dividing it by 100 is the same rate.
                        basePointsPerSar: data.basePointsPerSar
                            ?? (data.basePercentage != null ? Number(data.basePercentage) / 100 : ''),
                        useTiersForPercentage: data.useTiersForPercentage || false,
                        // The ladder round-trips as-is. Numbers are kept as strings
                        // for the inputs and coerced back in the payload.
                        percentageTiers: (data.percentageTiers || []).map(t => ({
                            tierName: t?.tierName ?? '',
                            pointsPerSar: t?.pointsPerSar
                                ?? (t?.percentage != null ? Number(t.percentage) / 100 : ''),
                            eligibilityPoints: t?.eligibilityPoints ?? '',
                            redeemPointsPerSar: t?.redeemPointsPerSar
                                ?? (Number(t?.pointToCurrency) > 0 ? 1 / Number(t.pointToCurrency) : ''),
                        })),
                        rewardOnStampsCount: data.rewardOnStampsCount || '',
                        // Type-aware, so a stray stampTiers JSON on a percentage row
                        // can't flip a toggle that is now gated on loyaltyType.
                        useStampTiers: data.loyaltyType === 'stamps' && (data.stampTiers?.length > 0),
                        stampTiers: data.stampTiers || [],
                        // eligibleMode: data.eligibleMode || 'all',
                        // NOTE: products/categories were previously assigned twice in
                        // this same object literal; the first pair was dead and read a
                        // field (eligibleCategoryIds) the API never returns.
                        products: (data.eligibleProducts || []).map(p => p.id),
                        categories: (data.eligibleCategories || []).map(c => c.id),
                        redeemableProductPercentage: data.redeemableProductPercentage || '',
                        redeemProducts: data.redeemableProductEntries?.map(entry => ({
                            productId: entry?.productId,
                            rewardType: entry?.rewardType || "both",
                        })) || [],
                        // redeemProducts: data.redeemableProductEntries?.map(entry => ({
                        //     productId: entry?.productId,
                        //     rewardType: entry?.rewardType
                        // })) || [],
                        // redeemProducts: (data.redeemableProducts || []).map(p => p.id),
                        isActive: data.isActive,
                        voucherExpiryDays: data.voucherExpiryDays || 30,
                        voucherExpiryPolicy: data.voucherExpiryPolicy || "forfeit",
                        rewardIncludesModifiers: data.rewardIncludesModifiers || false,
                        earnOnNetOfDiscounts: data.earnOnNetOfDiscounts || false,
                    });
                }
            }).finally(() => setLoading(false));
        }
    }, [loyaltyId, merchantId]);

    const handleInputChange = (e, key) => {
        const { type, value, checked } = e.target
        const isCheckbox = type === "checkbox"
        setLoyaltyData((prev) => ({ ...prev, [key]: isCheckbox ? checked : value }))

        if (
            ["eligibleLoyaltyAmount", "basePointsPerSar", "rewardOnStampsCount", "redeemableProductPercentage"].includes(key)
        ) {
            if (Number.parseFloat(value) < 0) {
                setLoyaltyData((prev) => ({ ...prev, [key]: "0" }))
            }
        }
    }

    const handleTiersToggle = (isChecked) => {
        setLoyaltyData((prev) => ({ ...prev, useTiersForPercentage: isChecked }))
    }

    const handleStampTiersToggle = (isChecked) => {
        setLoyaltyData((prev) => ({ ...prev, useStampTiers: isChecked }))
    }

    const addStampTierRow = () => {
        setLoyaltyData((prev) => ({
            ...prev,
            stampTiers: [...prev.stampTiers, { minAmount: "", stamps: "" }]
        }))
    }

    const removeStampTierRow = (index) => {
        setLoyaltyData((prev) => ({
            ...prev,
            stampTiers: prev.stampTiers.filter((_, i) => i !== index)
        }))
    }

    const handleStampTierChange = (index, key, value) => {
        setLoyaltyData((prev) => {
            const newTiers = [...prev.stampTiers]
            newTiers[index][key] = value
            return { ...prev, stampTiers: newTiers }
        })
    }

    // Percentage tiers — same shape as the stamp-tier handlers above, but the row
    // object is replaced rather than mutated in place.
    const addPercentageTierRow = () => {
        setLoyaltyData((prev) => ({
            ...prev,
            percentageTiers: [
                ...prev.percentageTiers,
                // First row is the mandatory base tier: what every customer resolves
                // to at 0 points. The API rejects a ladder without one.
                prev.percentageTiers.length === 0
                    ? { tierName: "Base", pointsPerSar: "", eligibilityPoints: "0", redeemPointsPerSar: "" }
                    : { tierName: "", pointsPerSar: "", eligibilityPoints: "", redeemPointsPerSar: "" }
            ]
        }))
    }

    const removePercentageTierRow = (index) => {
        setLoyaltyData((prev) => ({
            ...prev,
            percentageTiers: prev.percentageTiers.filter((_, i) => i !== index)
        }))
    }

    const handlePercentageTierChange = (index, key, value) => {
        setLoyaltyData((prev) => {
            const rows = [...prev.percentageTiers]
            rows[index] = { ...rows[index], [key]: value }
            return { ...prev, percentageTiers: rows }
        })
    }

    // Mirrors validatePercentageTiers in the API so the admin sees the same message
    // the server would return. Returns an error string, or null if valid.
    const validatePercentageTiersLocal = (tiers) => {
        if (!Array.isArray(tiers) || tiers.length === 0) return null
        const seenNames = new Set()
        const seenPoints = new Set()
        for (let i = 0; i < tiers.length; i++) {
            const t = tiers[i]
            // Messages name the ROW and the on-screen label, not the JSON path. An admin
            // reading "percentageTiers[2].redeemPointsPerSar must be a number >= 0" has to
            // count rows from zero and know a field name they are never shown.
            const row = i + 1
            const name = String(t.tierName || "").trim()
            if (!name) return `Row ${row}: give the tier a name.`
            if (seenNames.has(name.toLowerCase())) return `Two tiers are both called "${name}" — names must be unique.`
            seenNames.add(name.toLowerCase())

            const earn = Number(t.pointsPerSar)
            if (!Number.isFinite(earn) || earn < 0)
                return `Row ${row}: earn must be a number of points, 0 or more.`

            const pts = Number(t.eligibilityPoints)
            if (!Number.isInteger(pts) || pts < 0)
                return `Row ${row}: "unlocks at" must be a whole number of points, 0 or more.`
            if (seenPoints.has(pts)) return `Two tiers both unlock at ${pts} points — each tier needs its own threshold.`
            seenPoints.add(pts)

            const redeem = Number(t.redeemPointsPerSar)
            if (!Number.isFinite(redeem) || redeem < 0)
                return `Row ${row}: redeem must be a number of points, 0 or more.`
        }
        const base = tiers.find((t) => Number(t.eligibilityPoints) === 0)
        if (!base) return "One tier must unlock at 0 points — that is the tier every customer starts on."
        if (!(Number(base.redeemPointsPerSar) > 0))
            return "The tier that unlocks at 0 points must say how many points buy SAR 1, otherwise nobody can redeem."
        return null
    }

    const handleModalCheckboxChange = (product) => {
        setTempSelectedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(product.id)) newSet.delete(product.id);
            else {
                newSet.add(product.id);
                // Ensure product is in main list for display in dropdowns
                setProducts(current => {
                    return current.some(p => p.id === product.id) ? current : [...current, product];
                });
            }
            return newSet;
        });
    };

    const handleSelectAll = (e) => {
        const checked = e.target.checked;
        setTempSelectedIds(prev => {
            const newSet = new Set(prev);
            const newProductsToAdd = [];

            modalProducts.forEach(product => {
                if (checked) {
                    newSet.add(product.id);
                    newProductsToAdd.push(product);
                } else {
                    newSet.delete(product.id);
                }
            });

            if (checked && newProductsToAdd.length > 0) {
                setProducts(current => {
                    const existingIds = new Set(current.map(p => p.id));
                    const uniqueNewProducts = newProductsToAdd.filter(p => !existingIds.has(p.id));
                    if (uniqueNewProducts.length === 0) return current;
                    return [...current, ...uniqueNewProducts];
                });
            }

            return newSet;
        });
    };

    const handleProductSelectChange = (selectedOptions) => {
        if (selectedOptions.some(opt => opt.value === "SHOW_MORE")) {
            setModalType('eligible');
            setTempSelectedIds(new Set(loyaltyData.products));
            setModalSearch("");
            setModalCategory("");
            setModalPage(1);
            setProductModalOpen(true);
            return;
        }
        const values = selectedOptions ? selectedOptions.map((option) => option.value) : []
        setLoyaltyData((prev) => ({ ...prev, products: values }))
    }

    const handleCategorySelectChange = (selectedOptions) => {
        const values = selectedOptions ? selectedOptions.map((option) => option.value) : []
        setLoyaltyData((prev) => ({ ...prev, categories: values }))
    }

    const handleRedeemProductSelectChange = (selectedOptions) => {
        if (selectedOptions.some(opt => opt.value === "SHOW_MORE")) {
            setModalType('redeemable');
            const ids = loyaltyData.redeemProducts.map(rp => rp.productId);
            setTempSelectedIds(new Set(ids));
            setModalSearch("");
            setModalCategory("");
            setModalPage(1);
            setProductModalOpen(true);
            return;
        }
        const selectedIds = selectedOptions ? selectedOptions.map((option) => option.value) : []
        setLoyaltyData((prev) => {
            const currentProducts = prev.redeemProducts || []
            // Keep existing products that are still selected
            const updatedProducts = currentProducts.filter((p) => selectedIds.includes(p.productId))

            // Add new products
            selectedIds.forEach((id) => {
                if (!updatedProducts.some((p) => p.productId === id)) {
                    updatedProducts.push({ productId: id, rewardType: "both" })
                }
            })

            return { ...prev, redeemProducts: updatedProducts }
        })
    }

    const handleModalSave = () => {
        const selectedIds = Array.from(tempSelectedIds);

        if (modalType === 'eligible') {
            setLoyaltyData(prev => ({ ...prev, products: selectedIds }));
        } else if (modalType === 'redeemable') {
            setLoyaltyData(prev => {
                const currentProducts = prev.redeemProducts || [];
                // Keep existing settings for selected products
                const updatedProducts = currentProducts.filter(p => selectedIds.includes(p.productId));

                // Add new selections
                selectedIds.forEach(id => {
                    if (!updatedProducts.some(p => p.productId === id)) {
                        updatedProducts.push({ productId: id, rewardType: "both" });
                    }
                });
                return { ...prev, redeemProducts: updatedProducts };
            });
        }
        setProductModalOpen(false);
    };

    const handleRewardTypeChange = (productId, newType) => {
        setLoyaltyData((prev) => ({
            ...prev,
            redeemProducts: prev.redeemProducts.map((p) => (p.productId === productId ? { ...p, rewardType: newType } : p)),
        }))
    }

    const removeRedeemProduct = (productId) => {
        setLoyaltyData((prev) => ({
            ...prev,
            redeemProducts: prev.redeemProducts.filter((p) => p.productId !== productId),
        }))
    }

    const handleDiscard = () => {
        navigate("/merchant/loyalty")
    }

    const handleValidSubmit = async (e) => {
        e.preventDefault()

        if (!loyaltyData.name) {
            errorToast("Please fill in the name field.")
            return
        }

        const merchantIdToSubmit = userType === "merchant" ? Number(loggedInMerchantId) : Number(loyaltyData.merchantId);

        if (!merchantIdToSubmit) {
            errorToast("Please select a merchant.")
            return
        }

        if (loyaltyData.loyaltyType === 'percentage') {
            const tierError = validatePercentageTiersLocal(loyaltyData.percentageTiers)
            if (tierError) {
                errorToast(tierError)
                return
            }
        }

        try {
            setLoading(true)

            const payload = {
                id: loyaltyId,
                merchantId: merchantIdToSubmit,
                name: loyaltyData.name,
                arabicName: loyaltyData.arabicName,
                loyaltyType: loyaltyData.loyaltyType,
                isActive: loyaltyData.isActive,
                eligibleLoyaltyAmount: Number.parseFloat(loyaltyData.eligibleLoyaltyAmount) || 0,
                redeemableProducts: loyaltyData.redeemProducts || [],
                redeemableProductPercentage: Number.parseFloat(loyaltyData?.redeemableProductPercentage) || 0,
                rewardIncludesModifiers: loyaltyData.rewardIncludesModifiers,
                // Shared by both programme types — see the note on the toggle.
                earnOnNetOfDiscounts: !!loyaltyData.earnOnNetOfDiscounts,
                // eligibleMode: loyaltyData.eligibleMode,
            };

            if (loyaltyData.loyaltyType === 'stamps') {
                payload.eligibleProductIds = loyaltyData.products || [];
                payload.eligibleCategoryIds = loyaltyData.categories || [];
                payload.rewardOnStampsCount = Number.parseInt(loyaltyData.rewardOnStampsCount) || 0;
                payload.voucherExpiryDays = Number.parseInt(loyaltyData.voucherExpiryDays) || 30;
                payload.voucherExpiryPolicy = loyaltyData.voucherExpiryPolicy;
                payload.useStampTiers = loyaltyData.useStampTiers;
                if (loyaltyData.useStampTiers) {
                    payload.stampTiers = loyaltyData.stampTiers.map(t => ({
                        minAmount: Number(t.minAmount) || 0,
                        stamps: Number(t.stamps) || 0
                    }));
                }
            } else if (loyaltyData.loyaltyType === 'percentage') {
                payload.basePointsPerSar = Number.parseFloat(loyaltyData.basePointsPerSar) || 0;
                payload.useTiersForPercentage = loyaltyData.useTiersForPercentage;
                // Always sent for percentage, so removing every row clears the ladder.
                payload.percentageTiers = (loyaltyData.percentageTiers || []).map(t => ({
                    tierName: String(t.tierName || "").trim(),
                    pointsPerSar: Number(t.pointsPerSar) || 0,
                    eligibilityPoints: Number.parseInt(t.eligibilityPoints, 10) || 0,
                    redeemPointsPerSar: Number(t.redeemPointsPerSar) || 0,
                }));
            }

            // const response = await updateLoyalty(loyaltyId, payload);
            const response = await updateLoyalty(payload);

            if (response) {
                navigate("/merchant/loyalty")
            }
        } catch (error) {
            console.error("Error updating loyalty program:", error)
        } finally {
            setLoading(false)
        }
    }

    const fetchMerchantProducts = async () => {
        const merchantId = userType === "merchant" ? loggedInMerchantId : loyaltyData?.merchantId;
        if (merchantId) {
            const ResData = await getProductListing(merchantId);
            setProducts(ResData);
        }
    };

    const fetchMerchantCategories = async () => {
        const merchantId = userType === "merchant" ? loggedInMerchantId : loyaltyData?.merchantId;
        if (merchantId) {
            const ResData = await getCategoryListing(merchantId);
            setCategories(ResData);
        }
    };

    const fetchMerchantList = async () => {
        const merchants = await getMerchantListing();
        setMerchantData(merchants);
    };

    useEffect(() => {
        if (loyaltyData.merchantId) {
            fetchMerchantProducts();
            fetchMerchantCategories();
        }
    }, [loyaltyData.merchantId]);

    useEffect(() => {
        if (userType !== "merchant") {
            fetchMerchantList();
        }
    }, [userType]);

    const getDropdownOptions = (allProducts, selectedIds) => {
        if (!allProducts || allProducts.length === 0) return [];

        // Always show selected items
        const selectedItems = allProducts.filter(p => selectedIds.includes(p.id));

        // Show initial 10 items
        const initialItems = allProducts.slice(0, 10);

        // Combine and deduplicate
        const combined = [...initialItems, ...selectedItems];
        const uniqueItems = Array.from(new Map(combined.map(item => [item.id, item])).values());

        const options = uniqueItems.map(p => ({ value: p.id, label: p.title }));

        // Add Show More if we have more products than displayed
        if (allProducts.length > uniqueItems.length) {
            options.push({ value: "SHOW_MORE", label: "Show More..." });
        }

        return options;
    };

    const eligibleOptions = useMemo(() => getDropdownOptions(products, loyaltyData.products), [products, loyaltyData.products]);
    const redeemableIds = useMemo(() => loyaltyData.redeemProducts.map(rp => rp.productId), [loyaltyData.redeemProducts]);
    const redeemableOptions = useMemo(() => getDropdownOptions(products, redeemableIds), [products, redeemableIds]);

    const categoryOptions = useMemo(() => {
        if (!categories || categories.length === 0) return [];
        return categories.map(c => ({ value: c.id, label: c.name }));
    }, [categories]);

    const fetchModalProducts = async () => {
        setIsModalLoading(true);
        const merchantId = userType === "merchant" ? loggedInMerchantId : loyaltyData?.merchantId;
        const filters = {
            search: modalSearch,
            categoryId: modalCategory,
            isActive: true
        };
        const response = await getProductListing(merchantId, filters, modalPage, modalLimit);
        if (response && response.data) {
            setModalProducts(response.data);
            setModalTotalCount(response.totalCount);
        } else {
            setModalProducts([]);
            setModalTotalCount(0);
        }
        setIsModalLoading(false);
    };

    useEffect(() => {
        if (productModalOpen) {
            fetchModalProducts();
        }
    }, [productModalOpen, modalPage, modalSearch, modalCategory]);

    const modalTotalPages = Math.ceil(modalTotalCount / modalLimit);
    const handleModalPageChange = (page) => { if (page >= 1 && page <= modalTotalPages) setModalPage(page); };

    const renderModalPagination = () => {
        const pages = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, modalPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(modalTotalPages, startPage + maxPagesToShow - 1);

        if (endPage - startPage + 1 < maxPagesToShow) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }

        if (startPage > 1) {
            pages.push(
                <PaginationItem key="first">
                    <PaginationLink onClick={() => handleModalPageChange(1)}>1</PaginationLink>
                </PaginationItem>
            );
            if (startPage > 2) {
                pages.push(<PaginationItem key="start-ellipsis" disabled><PaginationLink>...</PaginationLink></PaginationItem>);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <PaginationItem active={i === modalPage} key={i}>
                    <PaginationLink onClick={() => handleModalPageChange(i)}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        if (endPage < modalTotalPages) {
            if (endPage < modalTotalPages - 1) {
                pages.push(<PaginationItem key="end-ellipsis" disabled><PaginationLink>...</PaginationLink></PaginationItem>);
            }
            pages.push(
                <PaginationItem key="last">
                    <PaginationLink onClick={() => handleModalPageChange(modalTotalPages)}>{modalTotalPages}</PaginationLink>
                </PaginationItem>
            );
        }
        return pages;
    };

    return (
        <Fragment>
            <Breadcrumb title="Edit Loyalty" parent="Loyalty" />

            <Container fluid={true}>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">
                {loading ? (
                    <Loader />
                ) : (
                    <Row>
                        <Col sm="12">
                            <Card style={{ borderRadius: 14, border: `1px solid ${C.border}`, boxShadow: C.shadow }}>
                                <CardHeader style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: "22px 24px", borderRadius: "14px 14px 0 0" }}>
                                    <h5 style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 4 }}>🎯 Edit Loyalty Program</h5>
                                    <p style={{ color: C.textMed, marginBottom: 0, fontSize: 13 }}>
                                        Configure your loyalty program settings and rewards
                                    </p>
                                </CardHeader>
                                <CardBody style={{ padding: "24px" }}>
                                    <Form className="needs-validation add-product-form" onSubmit={handleValidSubmit}>
                                        {/* Basic Information Section */}
                                        <div className="mb-4">
                                            <h6
                                                className="mb-3"
                                                style={{
                                                    fontSize: "16px",
                                                    fontWeight: "600",
                                                    color: "#2c3e50",
                                                    borderBottom: "2px solid #e9ecef",
                                                    paddingBottom: "8px",
                                                }}
                                            >
                                                Loyalty
                                            </h6>
                                            <Row>
                                                <Col md="6">
                                                    <FormGroup>
                                                        <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                            Program Name <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            className="form-control"
                                                            type="text"
                                                            required
                                                            placeholder="Enter loyalty program name"
                                                            value={loyaltyData.name}
                                                            onChange={(e) => handleInputChange(e, "name")}
                                                            style={{
                                                                borderRadius: "6px",
                                                                border: "1px solid #dee2e6",
                                                                padding: "10px 14px",
                                                            }}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                {/* arabicName was in state and in the payload but had no input here, so
                                                    every save on this form overwrote the stored Arabic name. */}
                                                <Col md="6">
                                                    <FormGroup>
                                                        <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                            Program Name (Arabic)
                                                        </Label>
                                                        <Input
                                                            className="form-control"
                                                            type="text"
                                                            placeholder="اسم برنامج الولاء"
                                                            value={loyaltyData.arabicName}
                                                            onChange={(e) => handleInputChange(e, "arabicName")}
                                                            style={{
                                                                borderRadius: "6px",
                                                                border: "1px solid #dee2e6",
                                                                padding: "10px 14px",
                                                            }}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md="6">
                                                    <FormGroup>
                                                        <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                            Loyalty Type <span className="text-danger">*</span>
                                                        </Label>
                                                        <div className="position-relative">
                                                            <select
                                                                className="form-control"
                                                                disabled={true}
                                                                value={loyaltyData.loyaltyType}
                                                                onChange={(e) => handleInputChange(e, "loyaltyType")}
                                                                style={{
                                                                    borderRadius: "6px",
                                                                    border: "1px solid #dee2e6",
                                                                    padding: "10px 14px",
                                                                    appearance: "none",
                                                                }}
                                                            >
                                                                <option value="">Select Type</option>
                                                                <option value="percentage">Percentage</option>
                                                                <option value="stamps">Stamps</option>
                                                            </select>
                                                            <span
                                                                style={{
                                                                    position: "absolute",
                                                                    top: "50%",
                                                                    right: "14px",
                                                                    transform: "translateY(-50%)",
                                                                    pointerEvents: "none",
                                                                }}
                                                            >
                                                                <ChevronDown size={18} />
                                                            </span>
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col md="6">
                                                    <FormGroup>
                                                        <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                            Active
                                                        </Label>
                                                        <div className="mt-2">
                                                            <ToggleSwitch
                                                                checked={loyaltyData.isActive}
                                                                handleToggle={(isChecked) => setLoyaltyData((prev) => ({ ...prev, isActive: isChecked }))}
                                                            />
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                                {/* Gated on the type: this toggle and its table used to render on
                                                    percentage programs too, where handleValidSubmit silently discarded
                                                    whatever was entered. */}
                                                {loyaltyData.loyaltyType === "stamps" && (
                                                    <Col md="6">
                                                        <FormGroup>
                                                            <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                                Use Tiers For Stamps
                                                            </Label>
                                                            <div className="mt-2">
                                                                <ToggleSwitch
                                                                    checked={loyaltyData.useStampTiers}
                                                                    handleToggle={handleStampTiersToggle}
                                                                />
                                                            </div>
                                                        </FormGroup>
                                                    </Col>
                                                )}
                                            </Row>

                                            {loyaltyData.loyaltyType === "stamps" && loyaltyData.useStampTiers && (
                                                <Col md="12">
                                                    <Table bordered hover>
                                                        <thead>
                                                            <tr>
                                                                <th>Minimum Amount</th>
                                                                <th>Stamps</th>
                                                                <th className="text-center">Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {loyaltyData.stampTiers.map((tier, index) => (
                                                                <tr key={index}>
                                                                    <td>
                                                                        <Input
                                                                            type="number"
                                                                            value={tier.minAmount}
                                                                            onChange={(e) => handleStampTierChange(index, "minAmount", e.target.value)}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <Input
                                                                            type="number"
                                                                            value={tier.stamps}
                                                                            onChange={(e) => handleStampTierChange(index, "stamps", e.target.value)}
                                                                        />
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <Button color="danger" size="sm" onClick={() => removeStampTierRow(index)}>
                                                                            <Trash2 size={14} />
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                    <Button color="success" size="sm" onClick={addStampTierRow} className="mb-3">
                                                        + Add Tier
                                                    </Button>
                                                </Col>
                                            )}

                                            <Row>
                                                <Col md="6">
                                                    <FormGroup>
                                                        <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                            Eligible Loyalty Amount
                                                        </Label>
                                                        <Input
                                                            className="form-control"
                                                            min="0"
                                                            type="number"
                                                            placeholder="Enter amount"
                                                            value={loyaltyData.eligibleLoyaltyAmount}
                                                            onChange={(e) => handleInputChange(e, "eligibleLoyaltyAmount")}
                                                            style={{
                                                                borderRadius: "6px",
                                                                border: "1px solid #dee2e6",
                                                                padding: "10px 14px",
                                                            }}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                {userType !== "merchant" && (
                                                    <Col md="6">
                                                        <FormGroup>
                                                            <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                                Merchant <span className="text-danger">*</span>
                                                            </Label>
                                                            <div className="position-relative">
                                                                <select
                                                                    className="form-control"
                                                                    value={loyaltyData.merchantId}
                                                                    onChange={(e) => handleInputChange(e, "merchantId")}
                                                                    style={{
                                                                        borderRadius: "6px",
                                                                        border: "1px solid #dee2e6",
                                                                        padding: "10px 14px",
                                                                        appearance: "none",
                                                                    }}
                                                                    disabled
                                                                >
                                                                    <option value="">Select Merchant</option>
                                                                    {merchantData?.length > 0 ? (
                                                                        merchantData?.map((merchant) => (
                                                                            <option key={merchant.id} value={merchant.id}>
                                                                                {merchant.name}
                                                                            </option>
                                                                        ))
                                                                    ) : (
                                                                        <option value="">No Merchant Available</option>
                                                                    )}
                                                                </select>
                                                                <span
                                                                    style={{
                                                                        position: "absolute",
                                                                        top: "50%",
                                                                        right: "14px",
                                                                        transform: "translateY(-50%)",
                                                                        pointerEvents: "none",
                                                                    }}
                                                                >
                                                                    <ChevronDown size={18} />
                                                                </span>
                                                            </div>
                                                        </FormGroup>
                                                    </Col>
                                                )}

                                                <Col md="6">
                                                    <FormGroup>
                                                        <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                            Earn after discounts
                                                        </Label>
                                                        <div className="mt-2">
                                                            <ToggleSwitch
                                                                checked={loyaltyData.earnOnNetOfDiscounts}
                                                                handleToggle={(v) => setLoyaltyData((prev) => ({ ...prev, earnOnNetOfDiscounts: v }))}
                                                            />
                                                        </div>
                                                        {/*
                                                          OUTSIDE the percentage block on purpose. It governs the earning BASE, and
                                                          prepareLoyaltyForOrder computes that base with the same function for both
                                                          programme types — calculateEligibleSubtotalForStamps passes this flag
                                                          through for points programmes too, despite its name. Hidden from stamps
                                                          merchants it would be unreachable for half the people it affects.

                                                          Only affects orders placed after the change: the flag is frozen into each
                                                          order's loyaltySnapshot, so nothing already placed moves.
                                                        */}
                                                        <span style={{ fontSize: 12, color: "#6B7280", marginTop: 4, display: "block" }}>
                                                            Off: earning is counted on the full basket. On: discounts and coupons come
                                                            off first, so a discounted order earns less. Applies to points and stamps
                                                            alike; existing orders are unaffected either way.
                                                        </span>
                                                    </FormGroup>
                                                </Col>

                                                {loyaltyData.loyaltyType === "percentage" && (
                                                    <>
                                                        <Col md="6">
                                                            <FormGroup>
                                                                <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                                    Earn rate (points per SAR)
                                                                </Label>
                                                                <Input
                                                                    className="form-control"
                                                                    min="0"
                                                                    type="number"
                                                                    placeholder="e.g. 10"
                                                                    value={loyaltyData.basePointsPerSar}
                                                                    onChange={(e) => handleInputChange(e, "basePointsPerSar")}
                                                                    style={{
                                                                        borderRadius: "6px",
                                                                        border: "1px solid #dee2e6",
                                                                        padding: "10px 14px",
                                                                    }}
                                                                />
                                                                {/* Same quantity the tier table's "Earn rate" column holds — this is
                                                                    the fallback resolveEarnPercentage() uses when tiers are off. Two
                                                                    names for one number is what made this screen hard to read. */}
                                                                <span style={{ fontSize: 12, color: "#6B7280", marginTop: 4, display: "block" }}>
                                                                    Used when tiers are off. With tiers on, each tier's own rate applies.
                                                                </span>
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md="6">
                                                            <FormGroup>
                                                                <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                                    Use Tiers For Percentage
                                                                </Label>
                                                                <div className="mt-2">
                                                                    <ToggleSwitch
                                                                        checked={loyaltyData.useTiersForPercentage}
                                                                        handleToggle={handleTiersToggle}
                                                                    />
                                                                </div>
                                                            </FormGroup>
                                                        </Col>

                                                        <Col md="12">
                                                            <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                                Loyalty Tiers
                                                            </Label>
                                                            <Table bordered hover>
                                                                <thead>
                                                                    <tr>
                                                                        <th>Tier Name</th>
                                                                        {/*
                                                                          Named for what they DO, not for the column they write to.
                                                                          "Eligibility Points" in particular read as "points you get
                                                                          for being eligible" — it is the opposite: the lifetime total
                                                                          a customer must PASS to reach the tier. The stored field
                                                                          names are unchanged; this is wording only.
                                                                        */}
                                                                        <th>Earn<br /><small className="text-muted">(points per SAR)</small></th>
                                                                        <th>Unlocks at<br /><small className="text-muted">(lifetime points)</small></th>
                                                                        <th>Redeem<br /><small className="text-muted">(points for SAR 1)</small></th>
                                                                        <th>Worth<br /><small className="text-muted">(of spend)</small></th>
                                                                        <th className="text-center">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {loyaltyData.percentageTiers.map((tier, index) => (
                                                                        <tr key={index}>
                                                                            <td>
                                                                                <Input
                                                                                    type="text"
                                                                                    placeholder="e.g. Bronze"
                                                                                    value={tier.tierName}
                                                                                    onChange={(e) => handlePercentageTierChange(index, "tierName", e.target.value)}
                                                                                />
                                                                            </td>
                                                                            <td>
                                                                                {/* No max: the old 0-100 bound was a side effect of the rate
                                                                                    being a percentage, and it capped earning at one point
                                                                                    per SAR. */}
                                                                                <Input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    placeholder="e.g. 10"
                                                                                    value={tier.pointsPerSar}
                                                                                    onChange={(e) => handlePercentageTierChange(index, "pointsPerSar", e.target.value)}
                                                                                />
                                                                            </td>
                                                                            <td>
                                                                                <Input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    placeholder="e.g. 500"
                                                                                    value={tier.eligibilityPoints}
                                                                                    onChange={(e) => handlePercentageTierChange(index, "eligibilityPoints", e.target.value)}
                                                                                />
                                                                            </td>
                                                                            <td>
                                                                                <Input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    placeholder="e.g. 200"
                                                                                    value={tier.redeemPointsPerSar}
                                                                                    onChange={(e) => handlePercentageTierChange(index, "redeemPointsPerSar", e.target.value)}
                                                                                />
                                                                            </td>
                                                                            <td>
                                                                                {/* The number that costs money. Read-only and derived; see
                                                                                    cashbackPercent for why it is on screen at all. */}
                                                                                {cashbackPercent(tier.pointsPerSar, tier.redeemPointsPerSar)
                                                                                    ? <strong>{cashbackPercent(tier.pointsPerSar, tier.redeemPointsPerSar)}%</strong>
                                                                                    : <span className="text-muted">&mdash;</span>}
                                                                            </td>
                                                                            <td className="text-center">
                                                                                <Button color="danger" size="sm" onClick={() => removePercentageTierRow(index)}>
                                                                                    <Trash2 size={14} />
                                                                                </Button>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </Table>
                                                            <Button color="success" size="sm" onClick={addPercentageTierRow} className="mb-2">
                                                                + Add Tier
                                                            </Button>
                                                            <p style={{ fontSize: "12px", color: "#6B7280", marginBottom: "16px" }}>
                                                                <strong>Earn</strong> is points given per SAR spent: 10 turns a SAR 100
                                                                order into 1,000 points.{" "}
                                                                <strong>Redeem</strong> is how many points buy SAR 1 back.{" "}
                                                                <strong>Worth</strong> is the two of them divided — the actual discount
                                                                you are giving. Watch that column: raising Earn from 10 to 50 without
                                                                touching Redeem takes a 5% programme to 25%.{" "}
                                                                <strong>Unlocks at</strong> is the customer's LIFETIME points total — they
                                                                reach a tier the moment they pass it, so a tier change takes effect
                                                                immediately and is never stored on the customer. The tier that unlocks
                                                                at 0 applies to everyone who has not reached a higher one.
                                                            </p>
                                                        </Col>
                                                    </>
                                                )}

                                                {loyaltyData.loyaltyType === "stamps" && (
                                                    <>
                                                        <Col md="6">
                                                            <FormGroup>
                                                                <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                                    Reward On Stamps Count
                                                                </Label>
                                                                <Input
                                                                    className="form-control"
                                                                    min="0"
                                                                    type="number"
                                                                    placeholder="Enter stamp count"
                                                                    value={loyaltyData.rewardOnStampsCount}
                                                                    onChange={(e) => handleInputChange(e, "rewardOnStampsCount")}
                                                                    style={{
                                                                        borderRadius: "6px",
                                                                        border: "1px solid #dee2e6",
                                                                        padding: "10px 14px",
                                                                    }}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    </>
                                                )}
                                            </Row>

                                            {loyaltyData.loyaltyType === "stamps" && (
                                                <Row>
                                                    <Col md="6">
                                                        <FormGroup>
                                                            <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                                Products
                                                            </Label>
                                                            <MultiSelectField
                                                                options={eligibleOptions}
                                                                value={eligibleOptions.filter((opt) => loyaltyData.products.includes(opt.value))}
                                                                onChange={handleProductSelectChange}
                                                                placeholder="Search & Select Products..."
                                                                isDisabled={userType !== "merchant" && !loyaltyData.merchantId}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md="6">
                                                        <FormGroup>
                                                            <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                                Categories
                                                            </Label>
                                                            <MultiSelectField
                                                                options={categoryOptions}
                                                                value={categoryOptions.filter((opt) => loyaltyData.categories.includes(opt.value))}
                                                                onChange={handleCategorySelectChange}
                                                                placeholder="Search & Select Categories..."
                                                                isDisabled={userType !== "merchant" && !loyaltyData.merchantId}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                            )}
                                        </div>

                                        {/* Redemption Settings Section */}
                                        <div className="mb-4">
                                            <h6
                                                className="mb-3"
                                                style={{
                                                    fontSize: "16px",
                                                    fontWeight: "600",
                                                    color: "#2c3e50",
                                                    borderBottom: "2px solid #e9ecef",
                                                    paddingBottom: "8px",
                                                }}
                                            >
                                                Redeem
                                            </h6>
                                            <Row>
                                                <Col md="6">
                                                    <FormGroup>
                                                        <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                            Redeemable Product Percentage
                                                        </Label>
                                                        <Input
                                                            className="form-control"
                                                            type="number"
                                                            min="0"
                                                            placeholder="Enter percentage"
                                                            value={loyaltyData.redeemableProductPercentage}
                                                            onChange={(e) => handleInputChange(e, "redeemableProductPercentage")}
                                                            style={{
                                                                borderRadius: "6px",
                                                                border: "1px solid #dee2e6",
                                                                padding: "10px 14px",
                                                            }}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md="6">
                                                    <FormGroup>
                                                        <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                            Reward Includes Modifiers
                                                        </Label>
                                                        <div className="mt-2">
                                                            <ToggleSwitch
                                                                checked={loyaltyData.rewardIncludesModifiers}
                                                                handleToggle={(isChecked) => setLoyaltyData((prev) => ({ ...prev, rewardIncludesModifiers: isChecked }))}
                                                            />
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            {loyaltyData.loyaltyType === "stamps" && (
                                                <Row>
                                                    <Col md="6">
                                                        <FormGroup>
                                                            <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                                Voucher Expiry Days
                                                            </Label>
                                                            <Input
                                                                className="form-control"
                                                                type="number"
                                                                min="1"
                                                                max="360"
                                                                placeholder="Enter days"
                                                                value={loyaltyData.voucherExpiryDays}
                                                                onChange={(e) => handleInputChange(e, "voucherExpiryDays")}
                                                                style={{
                                                                    borderRadius: "6px",
                                                                    border: "1px solid #dee2e6",
                                                                    padding: "10px 14px",
                                                                }}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md="6">
                                                        <FormGroup>
                                                            <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                                Voucher Expiry Policy
                                                            </Label>
                                                            <Input
                                                                type="select"
                                                                value={loyaltyData.voucherExpiryPolicy}
                                                                onChange={(e) => handleInputChange(e, "voucherExpiryPolicy")}
                                                                style={{
                                                                    borderRadius: "6px",
                                                                    border: "1px solid #dee2e6",
                                                                    padding: "10px 14px",
                                                                }}
                                                            >
                                                                <option value="forfeit">Forfeit</option>
                                                                <option value="refund">Refund</option>
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                            )}
                                            <Row>
                                                <Col md="12">
                                                    <FormGroup>
                                                        <Label className="mb-2" style={{ fontWeight: "500", fontSize: "14px" }}>
                                                            Redeemable Products
                                                        </Label>
                                                        <div className="d-flex align-items-center">
                                                            <div style={{ flexGrow: 1 }}>
                                                                <MultiSelectField
                                                                    options={redeemableOptions}
                                                                    value={redeemableOptions.filter((opt) => loyaltyData.redeemProducts.some((rp) => rp.productId === opt.value))}
                                                                    onChange={handleRedeemProductSelectChange}
                                                                    placeholder="Search & Select Products..."
                                                                    isDisabled={userType !== "merchant" && !loyaltyData.merchantId}
                                                                />
                                                            </div>
                                                            {loyaltyData.redeemProducts.length > 0 && (
                                                                <Button
                                                                    type="button"
                                                                    color="primary"
                                                                    className="ms-2"
                                                                    onClick={() => setShowRedeemTable(!showRedeemTable)}
                                                                    title={showRedeemTable ? "Hide Table" : "Show Table"}
                                                                >
                                                                    {showRedeemTable ? <EyeOff size={16} /> : <Eye size={16} />}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </FormGroup>
                                                    {loyaltyData.redeemProducts?.length > 0 && showRedeemTable && (
                                                        <div className="table-responsive mt-3">
                                                            <Table bordered hover>
                                                                <thead>
                                                                    <tr>
                                                                        <th>Product Name</th>
                                                                        <th>Reward Type</th>
                                                                        <th className="text-center">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {loyaltyData.redeemProducts.map((item, index) => {
                                                                        const product = products?.find((p) => p?.id == item?.productId)
                                                                        return (
                                                                            <tr key={index}>
                                                                                {/* <td style={{ verticalAlign: "middle" }}>{product?.title}</td> */}
                                                                                <td
                                                                                    style={{
                                                                                        verticalAlign: "middle",
                                                                                        textDecoration: product?.is_active === false ? "line-through" : "none",
                                                                                        color: product?.is_active === false ? "#999" : "inherit",
                                                                                    }}
                                                                                >
                                                                                    {product?.title}
                                                                                </td>
                                                                                <td>
                                                                                    <Input
                                                                                        type="select"
                                                                                        value={item.rewardType}
                                                                                        onChange={(e) => handleRewardTypeChange(item.productId, e.target.value)}
                                                                                        style={{ fontSize: "14px" }}
                                                                                    >
                                                                                        <option value="both">Both</option>
                                                                                        <option value="pos">POS</option>
                                                                                        <option value="app">App</option>
                                                                                    </Input>
                                                                                </td>
                                                                                <td className="text-center" style={{ verticalAlign: "middle" }}>
                                                                                    <Button color="danger" size="sm" onClick={() => removeRedeemProduct(item.productId)}>
                                                                                        <Trash2 size={14} />
                                                                                    </Button>
                                                                                </td>
                                                                            </tr>
                                                                        )
                                                                    })}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    )}
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* Action Buttons */}
                                        <div
                                            className="d-flex justify-content-end gap-3 mt-4 pt-3"
                                            style={{ borderTop: "1px solid #e9ecef" }}
                                        >
                                            <Button
                                                onClick={handleDiscard}
                                                type="button"
                                                style={{
                                                    padding: "10px 28px",
                                                    borderRadius: 10,
                                                    fontWeight: 600,
                                                    fontSize: 13,
                                                    border: `1px solid ${C.border}`,
                                                    background: C.white,
                                                    color: C.textMed,
                                                }}
                                            >
                                                Discard
                                            </Button>
                                            <Button
                                                type="submit"
                                                style={{
                                                    padding: "10px 32px",
                                                    borderRadius: 10,
                                                    color: "white",
                                                    fontWeight: 700,
                                                    fontSize: 13,
                                                    border: "none",
                                                    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                                                    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
                                                }}
                                            >
                                                Update Program
                                            </Button>
                                        </div>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}
                </div>
            </Container>

            {/* Product Selection Modal */}
            <Modal isOpen={productModalOpen} toggle={() => setProductModalOpen(false)} size="lg">
                <ModalHeader toggle={() => setProductModalOpen(false)}>
                    Select {modalType === 'eligible' ? 'Eligible' : 'Redeemable'} Products
                </ModalHeader>
                <ModalBody>
                    <Row className="mb-3">
                        <Col md="6">
                            <Input
                                type="text"
                                placeholder="Search products..."
                                value={modalSearch}
                                onChange={(e) => { setModalSearch(e.target.value); setModalPage(1); }}
                            />
                        </Col>
                        <Col md="6">
                            <Input
                                type="select"
                                value={modalCategory}
                                onChange={(e) => { setModalCategory(e.target.value); setModalPage(1); }}
                            >
                                <option value="">All Categories</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </Input>
                        </Col>
                    </Row>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {isModalLoading ? <Loader /> : (
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead style={{ borderBottom: "2px solid #dee2e6" }}>
                                    <tr>
                                        <th style={{ ...tableHeaderStyle, width: "50px" }} className="text-center">
                                            <Input
                                                type="checkbox"
                                                onChange={handleSelectAll}
                                                checked={modalProducts.length > 0 && modalProducts.every(p => tempSelectedIds.has(p.id))}
                                                disabled={modalProducts.length === 0}
                                            />
                                        </th>
                                        <th style={tableHeaderStyle}>Product Name</th>
                                        <th style={tableHeaderStyle}>Category</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modalProducts.map(product => (
                                        <tr key={product.id} style={{ borderBottom: "1px solid #dee2e6" }}>
                                            <td style={tableCellStyle} className="text-center">
                                                <Input
                                                    type="checkbox"
                                                    checked={tempSelectedIds.has(product.id)}
                                                    onChange={() => handleModalCheckboxChange(product)}
                                                />
                                            </td>
                                            <td style={tableCellStyle}>{product.title}</td>
                                            <td style={tableCellStyle}>{product.Category?.name || product.CategoryId}</td>
                                        </tr>
                                    ))}
                                    {modalProducts.length === 0 && (
                                        <tr>
                                            <td colSpan="3" className="text-center">No products found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <div className="d-flex justify-content-center mt-3">
                        {modalTotalPages > 1 && (
                            <Pagination>
                                <PaginationItem disabled={modalPage <= 1}>
                                    <PaginationLink previous onClick={() => handleModalPageChange(modalPage - 1)} />
                                </PaginationItem>
                                {renderModalPagination()}
                                <PaginationItem disabled={modalPage >= modalTotalPages}>
                                    <PaginationLink next onClick={() => handleModalPageChange(modalPage + 1)} />
                                </PaginationItem>
                            </Pagination>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleModalSave}>Save Selection</Button>
                    <Button color="secondary" onClick={() => setProductModalOpen(false)}>Cancel</Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    )
}

const tableHeaderStyle = {
    padding: "10px",
    textAlign: "left",
    background: "#f2f2f2",
    borderBottom: "1px solid #dee2e6",
};

const tableCellStyle = {
    padding: "10px",
    textAlign: "left",
};

export default Edit_Loyalty